import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { reaction } from "mobx";
import { Clip, TransitionPointData, TransitionSideConfig } from '../Clip/Clip';
import './AudioClip.styles.scss';
import {CompositionStateStore} from "../../../../store/CompositionStateStore";
import WaveformDisplay from "../../../../../librettoEditor/modules/timeline/components/WaveformDisplay/WaveformDisplay";
import {CompositionService} from "../../../../services/CompositionService";
import {TimelineStore} from "../../../../store/TimelineStore";
import {useAssetStore} from "../../../../../storage/AssetStore";
import {ProjectAssetsStore} from "../../../../../state/ProjectAssetsStore";
import {ApplicationStore} from "../../../../store/ApplicationStore";
import {ScriptLayerStore} from "../../../../store/ScriptLayerStore";
import { TransitionConfig } from "../TransitionHandle";

interface AudioClipProps {
    clipId: string;
    layerId: string;
}

const normalizeState = (value: any) => String(value ?? '').toLowerCase();

const isSilentState = (value: any) => {
    const state = normalizeState(value);
    return state === 'silent' || state === 'not_needed';
};

const isTrackSilent = (track: any): boolean => {
    if (!track) return false;
    if (track?.isSilentAudio === true) return true;
    if (track?.hasAudioStream === false) return true;
    if (isSilentState(track?.transcriptionState)) return true;
    if (isSilentState(track?.enhancedTrackState)) return true;
    return false;
};

export const AudioClip: React.FC<AudioClipProps> = observer(({ clipId, layerId }) => {
    const [filename, setFilename] = useState<string | undefined>(CompositionStateStore.getClipTitle(clipId));
    const [thumbnail, setThumbnail] = useState<string | undefined>(CompositionStateStore.getThumbnailByClipId(clipId));
    const [peaks, setPeaks] = useState<Float32Array | null>(null);
    const [fadeInConfig, setFadeInConfig] = useState<TransitionConfig | undefined>(undefined);
    const [fadeOutConfig, setFadeOutConfig] = useState<TransitionConfig | undefined>(undefined);
    const [hasAdjacentBefore, setHasAdjacentBefore] = useState(false);
    const [hasAdjacentAfter, setHasAdjacentAfter] = useState(false);
    const { GetWaveformData } = useAssetStore();
    const waveformFetchSeq = useRef(0);
    const trackId = CompositionStateStore.clipIdToAssetId?.[clipId];

    useEffect(() => {
        const disposer = reaction(
            () => ({
                filename: CompositionStateStore.getClipTitle(clipId),
                thumbnail: CompositionStateStore.getThumbnailByClipId(clipId)
            }),
            ({ filename, thumbnail }) => {
                setFilename(filename);
                setThumbnail(thumbnail);
            },
            { fireImmediately: true }
        );

        return () => {
            disposer();
        };
    }, [clipId]);

    // Sync transition config from engine clip
    useEffect(() => {
        const clip = CompositionService.getClipById(clipId) as any;
        if (!clip) return;

        if (clip.fadeIn) {
            setFadeInConfig({
                type: clip.fadeIn.type ?? 'fade',
                durationSec: (clip.fadeIn.durationMs ?? 0) / 1000,
            });
        }
        if (clip.fadeOut) {
            setFadeOutConfig({
                type: clip.fadeOut.type ?? 'fade',
                durationSec: (clip.fadeOut.durationMs ?? 0) / 1000,
            });
        }
    }, [clipId]);

    // Check for adjacent clips (for crossfade option)
    useEffect(() => {
        const clip = CompositionService.getClipById(clipId) as any;
        if (!clip || !clip.track) return;

        const clips = clip.track.clips ?? [];
        const myIndex = clips.findIndex((c: any) => c.id === clipId);
        if (myIndex === -1) return;

        const myStart = clip.start?.seconds ?? 0;
        const myEnd = (clip.start?.seconds ?? 0) + (clip.timelineDuration?.seconds ?? 0);

        if (myIndex > 0) {
            const prevClip = clips[myIndex - 1];
            const prevEnd = (prevClip.start?.seconds ?? 0) + (prevClip.timelineDuration?.seconds ?? 0);
            setHasAdjacentBefore(Math.abs(myStart - prevEnd) <= 0.002);
        } else {
            setHasAdjacentBefore(false);
        }

        if (myIndex < clips.length - 1) {
            const nextClip = clips[myIndex + 1];
            const nextStart = nextClip.start?.seconds ?? 0;
            setHasAdjacentAfter(Math.abs(nextStart - myEnd) <= 0.002);
        } else {
            setHasAdjacentAfter(false);
        }
    }, [clipId]);

    // Compute transition points data for rendering
    const transitionPointsData = useMemo((): TransitionPointData[] => {
        const clip = CompositionService.getClipById(clipId) as any;
        if (!clip || !clip.transitionPoints || clip.transitionPoints.length === 0) {
            return [];
        }

        const inSec = clip.range?.[0]?.seconds ?? 0;
        const outSec = clip.range?.[1]?.seconds ?? 0;
        const clipDuration = outSec - inSec;
        if (clipDuration <= 0) return [];

        return clip.transitionPoints.map((tp: any) => {
            const fadeOutType = tp.transition?.fadeOut?.type ?? 'none';
            const fadeOutDur = (tp.transition?.fadeOut?.durationMs ?? 0) / 1000;
            const fadeInType = tp.transition?.fadeIn?.type ?? 'none';
            const fadeInDur = (tp.transition?.fadeIn?.durationMs ?? 0) / 1000;

            return {
                id: tp.id,
                position: Math.max(0, Math.min(1, (tp.srcTimeSec - inSec) / clipDuration)),
                fadeOut: { type: fadeOutType, durationSec: fadeOutDur },
                fadeIn: { type: fadeInType, durationSec: fadeInDur },
            };
        });
    }, [clipId, CompositionStateStore.clips?.[clipId]]);

    const handleTransitionPointConfigChange = useCallback((
        tpId: string,
        side: 'fadeOut' | 'fadeIn',
        config: TransitionSideConfig
    ) => {
        const clip = CompositionService.getClipById(clipId) as any;
        if (clip && clip.setTransitionPointConfig) {
            const tp = clip.transitionPoints?.find((t: any) => t.id === tpId);
            const currentTransition = tp?.transition ?? {};

            const newTransition = {
                ...currentTransition,
                [side]: {
                    type: config.type,
                    durationMs: config.durationSec * 1000,
                }
            };

            clip.setTransitionPointConfig(tpId, newTransition);
        }
    }, [clipId]);

    const handleTransitionPointRemove = useCallback((tpId: string) => {
        const clip = CompositionService.getClipById(clipId) as any;
        if (clip && clip.removeTransitionPoint) {
            clip.removeTransitionPoint(tpId);
        }
    }, [clipId]);

    // Track Studio Sound state for waveform selection
    const [studioSoundEnabled, setStudioSoundEnabled] = useState(() => {
        const clip = CompositionService.getClipById(clipId) as any;
        return Boolean(clip?.studioSoundEnabled);
    });

    // Listen for studioSoundChanged events from the engine clip
    useEffect(() => {
        const clip = CompositionService.getClipById(clipId) as any;
        if (!clip) return;

        // Event is a CustomEvent - data is in event.detail
        const handleStudioSoundChanged = (event: any) => {
            const data = event?.detail ?? event;
            setStudioSoundEnabled(Boolean(data?.enabled));
        };

        const listenerId = clip.on?.('studioSoundChanged', handleStudioSoundChanged);

        // Initialize from current state
        if (typeof clip.studioSoundEnabled === 'boolean') {
            setStudioSoundEnabled(Boolean(clip.studioSoundEnabled));
        }

        return () => {
            if (listenerId) clip.off?.(listenerId);
        };
    }, [clipId]);

    // Re-render when waveform becomes available (react to hasWaveform or enhancedTrackState)
    useEffect(() => {
        const seq = ++waveformFetchSeq.current;
        if (!trackId) return;
        const disposer = reaction(
            () => {
                const t = ProjectAssetsStore.projectTracks.find((x: any) => x.id === trackId || x.trackId === trackId);
                const useDenoised = studioSoundEnabled && !isTrackSilent(t);
                return {
                    hasWaveform: useDenoised
                        ? t?.enhancedTrackState === 'ready'
                        : t?.hasWaveform === true,
                    useDenoised,
                };
            },
            async ({ hasWaveform, useDenoised }) => {
                if (hasWaveform) {
                    let data: Float32Array;
                    try {
                        data = await GetWaveformData({ trackId, denoised: useDenoised });
                    } catch {
                        return;
                    }
                    if (waveformFetchSeq.current !== seq) return;
                    setPeaks(data && data.length ? data : null);
                }
            },
            { fireImmediately: true }
        );
        return () => { disposer(); };
    }, [clipId, trackId, studioSoundEnabled]);

    const duration = CompositionStateStore.clips?.[clipId]?.trimmedDuration ?? 0;
    const clipGain = ScriptLayerStore.getClipGain(clipId);
    const engineClipForPeaks: any = CompositionService.getClipById(clipId);
    const clipWindowKey = `${engineClipForPeaks?.range?.[0]?.seconds ?? 0}:${engineClipForPeaks?.range?.[1]?.seconds ?? 0}:${engineClipForPeaks?.playbackRate ?? 1}:${Array.isArray(engineClipForPeaks?.cuts) ? engineClipForPeaks.cuts.length : 0}`;

    // Compute displayPeaks.
    // IMPORTANT: memoize so WaveSurfer isn't destroyed/re-created on every render (e.g. when selecting a range).
    const displayPeaks = useMemo(() => {
        if (!peaks || !peaks.length) return null;
        const clip: any = engineClipForPeaks;
        if (!clip) return peaks;
        const mediaDur = clip.duration?.seconds ?? 0;
        if (!mediaDur) return peaks;
        const segs: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> = clip.segments ?? [];
        if (Array.isArray(segs) && segs.length > 0) {
            const parts: Float32Array[] = [];
            let total = 0;
            for (const seg of segs) {
                const s = Math.max(0, Math.min(mediaDur, seg.srcStartSec ?? 0));
                const e = Math.max(0, Math.min(mediaDur, seg.srcEndSec ?? 0));
                if (e <= s) continue;
                const i0 = Math.max(0, Math.floor((s / mediaDur) * peaks.length));
                const i1 = Math.min(peaks.length, Math.floor((e / mediaDur) * peaks.length));
                if (i1 > i0) {
                    const sub = peaks.subarray(i0, i1);
                    parts.push(sub);
                    total += sub.length;
                }
            }
            if (!parts.length) return new Float32Array();
            const out = new Float32Array(total);
            let cursor = 0;
            for (const part of parts) {
                out.set(part, cursor);
                cursor += part.length;
            }
            return out;
        }
        const baseIn = clip.range?.[0]?.seconds ?? 0;
        const baseOut = clip.range?.[1]?.seconds ?? mediaDur;
        const inSec = Math.min(baseOut, Math.max(0, baseIn));
        const outSec = Math.max(inSec, Math.min(mediaDur, baseOut));
        if (outSec <= inSec) return new Float32Array();
        const startIdx = Math.max(0, Math.floor((inSec / mediaDur) * peaks.length));
        const endIdx = Math.min(peaks.length, Math.floor((outSec / mediaDur) * peaks.length));
        return peaks.subarray(startIdx, Math.max(startIdx, endIdx));
    }, [clipId, peaks, duration, clipWindowKey]);

    const handleFadeInChange = useCallback((config: TransitionConfig) => {
        setFadeInConfig(config);
        const clip = CompositionService.getClipById(clipId) as any;
        if (clip) {
            clip.fadeIn = { type: config.type, durationMs: config.durationSec * 1000 };
        }
    }, [clipId]);

    const handleFadeOutChange = useCallback((config: TransitionConfig) => {
        setFadeOutConfig(config);
        const clip = CompositionService.getClipById(clipId) as any;
        if (clip) {
            clip.fadeOut = { type: config.type, durationMs: config.durationSec * 1000 };
            if (config.type === 'crossfade' && hasAdjacentAfter) {
                const clips = clip.track?.clips ?? [];
                const myIndex = clips.findIndex((c: any) => c.id === clipId);
                if (myIndex >= 0 && myIndex < clips.length - 1) {
                    clips[myIndex + 1].fadeIn = { type: 'crossfade', durationMs: config.durationSec * 1000 };
                }
            }
        }
    }, [clipId, hasAdjacentAfter]);

    // Check if waveform is processing (no peaks yet)
    const isProcessing = !displayPeaks && duration > 0;
    const selectedRange =
        ApplicationStore.selectedClipId === clipId ? ApplicationStore.selectedRange : null;

    return (
        <Clip
            clipId={clipId}
            layerId={layerId}
            filename={filename}
            thumbnail={null}
            backgroundColor={"227, 251, 250"}
            showAudioTransitions={true}
            fadeInConfig={fadeInConfig}
            fadeOutConfig={fadeOutConfig}
            hasAdjacentBefore={hasAdjacentBefore}
            hasAdjacentAfter={hasAdjacentAfter}
            onFadeInChange={handleFadeInChange}
            onFadeOutChange={handleFadeOutChange}
            transitionPoints={transitionPointsData}
            onTransitionPointConfigChange={handleTransitionPointConfigChange}
            onTransitionPointRemove={handleTransitionPointRemove}
        >
            {displayPeaks && duration > 0 && (
                <div style={{ position: 'absolute', inset: 0, opacity: 0.7 }}>
                    <WaveformDisplay
                        clipId={clipId}
                        peaks={displayPeaks}
                        duration={duration}
                        rangeEnabled={ApplicationStore.rangeToolEnabled}
                        isAudioClip={true}
                        selectedRange={selectedRange}
                        gain={clipGain}
                    />
                </div>
            )}
            {isProcessing && (
                <div style={{
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 6px',
                    backgroundColor: 'rgba(55, 65, 81, 0.85)',
                    borderRadius: '4px',
                    zIndex: 10,
                }}>
                    <div style={{
                        width: '10px',
                        height: '10px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTopColor: '#fff',
                        borderRadius: '50%',
                        animation: 'clip-spin 1s linear infinite',
                    }} />
                    <span style={{
                        color: '#fff',
                        fontSize: '10px',
                        fontWeight: 500,
                    }}>Processing...</span>
                </div>
            )}
            <style>{`
                @keyframes clip-spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </Clip>
    );
});

export default AudioClip;
